/**
 * Calmy Club drawer behaviour.
 *
 * Open/close, focus trap, Escape and focus restore are calmy-core.js's — this
 * file adds only what is specific to the club: tabs, the retry, and the
 * redemption REQUEST.
 *
 * It never decides anything about money. It does not read a balance, compute
 * one, cache one, or write one. It asks the app-proxy endpoint named in
 * snippets/calmy-club-contract.liquid and renders whatever comes back. With no
 * backend deployed every redeem button is already `disabled` in the markup, so
 * the path below is unreachable until one exists — it is written now so the
 * contract has a client that matches it.
 */
(function () {
  /* Editable wording. Sources: Theme settings > "Text: ..." groups,
     serialised by snippets/calmy-strings.liquid. The second argument is
     the wording this file shipped with and is used when the snippet is
     absent or the setting is blank. */
  var CT = function (k, f) { return window.CalmyT ? window.CalmyT(k, f) : f; };

  "use strict";
  if (window.__calmyClub) return;
  window.__calmyClub = true;

  var ENDPOINT = "/apps/calmy-club/redeem";
  var CLAIM = "/apps/calmy-club/offers/claim";
  var VISIT = "/apps/calmy-club/visit";

  /* ── countdowns ──────────────────────────────────────────────────────────
     THE DEADLINE IS AN ABSOLUTE INSTANT, rendered by Liquid as a unix second
     from the customer's own stored assignment. Nothing here starts, stores or
     restarts a timer, so a refresh, a re-login and a re-open all display the
     same remaining time. Ticking is only redrawing that difference.

     One interval for the whole page rather than one per card: a drawer with
     eight offers should cost one timer, not eight. */
  var clocks = [];
  var ticking = 0;

  function fmt(secs) {
    if (secs <= 0) return null;
    var d = Math.floor(secs / 86400);
    var h = Math.floor((secs % 86400) / 3600);
    var m = Math.floor((secs % 3600) / 60);
    var sec = secs % 60;
    /* Days while there are days, seconds only in the last hour: a counter
       ticking down from eleven days is noise, and one in the final minutes
       is the thing somebody is actually watching. */
    if (d > 0) return d + "d " + h + "h";
    if (h > 0) return h + "h " + m + "m";
    return m + "m " + (sec < 10 ? "0" : "") + sec + "s";
  }

  function paint() {
    var now = Math.floor(Date.now() / 1000);
    var live = 0;
    for (var i = 0; i < clocks.length; i++) {
      var el = clocks[i];
      if (!el.isConnected) continue;
      var until = parseInt(el.getAttribute("data-calmy-club-until"), 10) || 0;
      var out = el.querySelector("[data-calmy-club-clock]");
      if (!out) continue;
      var left = until - now;
      var txt = fmt(left);
      if (txt === null) {
        /* Expired WHILE OPEN. The card is marked, the countdown says so, and
           the controls that would spend it are taken away. The server refuses
           it as well; this is the half the person can see. */
        if (!el.hasAttribute("data-expired")) {
          el.setAttribute("data-expired", "1");
          var card = el.closest(".cclub__o, .cclub__w");
          if (card) {
            card.classList.add(card.className.indexOf("cclub__w") >= 0 ? "is-spent" : "is-expired");
            var claim = card.querySelector("[data-calmy-club-claim]");
            if (claim) { claim.disabled = true; claim.textContent = el.getAttribute("data-expired-label") || "Expired"; }
            var copy = card.querySelector("[data-calmy-club-copy]");
            if (copy) copy.disabled = true;
          }
        }
        out.textContent = el.getAttribute("data-expired-text") || "Expired";
        continue;
      }
      out.textContent = txt;
      live++;
    }
    if (!live && ticking) { window.clearInterval(ticking); ticking = 0; }
  }

  function initClocks(scope) {
    var found = [].slice.call(scope.querySelectorAll("[data-calmy-club-until]"));
    if (!found.length) return;
    found.forEach(function (el) { if (clocks.indexOf(el) < 0) clocks.push(el); });
    paint();
    if (!ticking) ticking = window.setInterval(paint, 1000);
  }

  /* ── copy ────────────────────────────────────────────────────────────────
     A refused clipboard selects the code and says nothing about success. */
  function initCopy(scope) {
    scope.querySelectorAll("[data-calmy-club-copy]").forEach(function (btn) {
      if (btn.__calmyCopy) return;
      btn.__calmyCopy = true;
      btn.addEventListener("click", function () {
        var row = btn.closest(".cclub__ocode");
        var codeEl = row && row.querySelector("[data-calmy-club-code]");
        if (!codeEl) return;
        var text = codeEl.textContent.trim();
        var idle = btn.textContent;
        function ok() {
          btn.textContent = btn.getAttribute("data-done") || "Copied";
          btn.classList.add("is-done");
          window.setTimeout(function () { btn.textContent = idle; btn.classList.remove("is-done"); }, 2000);
        }
        function fail() {
          try {
            var r = document.createRange();
            r.selectNodeContents(codeEl);
            var sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(r);
          } catch (e) {}
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(ok, fail);
        } else { fail(); }
      });
    });
  }

  /* ---- the visit ping ----
     THIS IS A SIGNAL, NOT A COUNT. It tells the server "this member loaded a
     page"; the server decides whether that is a new day, whether three of
     them have happened inside the window, whether the cooldown has passed and
     whether anything should be issued. Nothing here counts, stores or
     remembers a visit, because a number a browser keeps is a number a
     customer can edit into a coupon.

     ONCE PER PAGE LOAD, and only on the first drawer initialised — a second
     tab is a second page load and the server dedupes by day, which is the
     only place that dedupe is safe.

     THREE GATES before it fires at all:
       · the programme is live
       · the visitor is a signed-in member, so the proxy can resolve them
       · consent allows it, where the shop asks for consent

     With the endpoint absent this never runs, because the programme is not
     live. It is deliberately silent: a failed ping is not a customer-facing
     event and must never surface as an error in the drawer. */
  var visitPinged = false;
  function pingVisit(root) {
    if (visitPinged) return;
    if (!root.hasAttribute("data-club-live") || !root.hasAttribute("data-club-member")) return;

    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (privacy && typeof privacy.analyticsProcessingAllowed === "function") {
      try {
        if (!privacy.analyticsProcessingAllowed()) return;
      } catch (e) { return; }
    }

    visitPinged = true;
    try {
      fetch(VISIT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        keepalive: true,
        body: "{}"
      }).catch(function () {});
    } catch (e) {}
  }

  function init(root) {
    if (root.__calmyClubDone) return;
    root.__calmyClubDone = true;

    /* ---- tabs ---- */
    /* Scoped to the tablist. The empty wallet's "See rewards" button carries
       the same attribute so it can switch tabs, but it is NOT a tab — pulling
       it into the roving-tabindex set would put a fourth stop in a
       three-stop widget and break arrow-key order. */
    var tabs = [].slice.call(root.querySelectorAll('[role="tablist"] [data-calmy-club-tab]'));
    var panels = [].slice.call(root.querySelectorAll("[data-calmy-club-panel]"));

    function select(key, focus) {
      tabs.forEach(function (t) {
        var on = t.getAttribute("data-calmy-club-tab") === key;
        t.setAttribute("aria-selected", on ? "true" : "false");
        t.tabIndex = on ? 0 : -1;
        if (on && focus) t.focus();
      });
      panels.forEach(function (p) {
        p.hidden = p.getAttribute("data-calmy-club-panel") !== key;
      });
    }
    tabs.forEach(function (t, i) {
      t.addEventListener("click", function () { select(t.getAttribute("data-calmy-club-tab")); });
      t.addEventListener("keydown", function (e) {
        var n = null;
        if (e.key === "ArrowRight") n = (i + 1) % tabs.length;
        else if (e.key === "ArrowLeft") n = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === "Home") n = 0;
        else if (e.key === "End") n = tabs.length - 1;
        if (n === null) return;
        e.preventDefault();
        select(tabs[n].getAttribute("data-calmy-club-tab"), true);
      });
    });

    /* Anything outside the tablist that asks for a tab — the empty wallet's
       way back to Rewards. */
    root.querySelectorAll('[data-calmy-club-tab]:not([role="tab"])').forEach(function (b) {
      b.addEventListener("click", function () { select(b.getAttribute("data-calmy-club-tab"), true); });
    });

    /* ---- retry ---- */
    var retry = root.querySelector("[data-calmy-club-retry]");
    if (retry) {
      retry.addEventListener("click", function () {
        /* The balance is rendered by Liquid from the customer's metafields, so
           the only honest "try again" is to ask the server for the page
           again. Nothing is guessed client-side. */
        retry.disabled = true;
        window.location.reload();
      });
    }

    /* ---- focus on close ----
       calmy-core restores focus to whatever opened the panel. For the club
       that trigger sits INSIDE the navigation drawer, which core closes on
       the way in — so by the time the club closes, its trigger is in a hidden
       inert subtree, focusing it does nothing, and keyboard focus falls to
       <body>. The next Tab then starts from the top of the page.

       Core is right to try the trigger first. This only catches the case
       where that could not have worked.

       CONDITION, NOT A DELAY. The browser blurs the element inside the newly
       inert panel asynchronously, and a single frame was landing before that
       happened — the check saw a perfectly good activeElement, returned, and
       focus fell to body a moment later. So this asks the real question,
       "is focus somewhere a keyboard can actually continue from", and polls a
       few frames for it to settle rather than guessing one interval. */
    if (window.MutationObserver) {
      new MutationObserver(function () {
        if (root.classList.contains("is-on")) return;

        var tries = 0;
        (function settle() {
          var a = document.activeElement;
          var stranded = !a || a === document.body ||
                         a.offsetParent === null ||
                         (a.closest && a.closest("[inert]"));
          if (!stranded) return;                 // focus is fine, leave it
          if (++tries < 12) return window.requestAnimationFrame(settle);

          /* In order of "closest to where the club lives", each checked for
             being visible and outside any inert subtree — the hamburger is
             display:none above 1150px, so one fallback would have covered a
             single breakpoint only. */
          var candidates = [
            document.querySelector('[data-calmy-open="[data-calmy-club]"]'),
            document.querySelector(".chead__btn--account"),
            document.querySelector("[data-calmy-hamburger]")
          ];
          for (var i = 0; i < candidates.length; i++) {
            var c = candidates[i];
            if (c && c.offsetParent !== null && !(c.closest && c.closest("[inert]"))) {
              try { c.focus({ preventScroll: true }); } catch (e) { c.focus(); }
              return;
            }
          }
        })();
      }).observe(root, { attributes: true, attributeFilter: ["class"] });
    }

    initClocks(root);
    initCopy(root);
    initNotify(root);
    pingVisit(root);

    /* ---- claiming a personal offer ----
       The same shape as redemption and for the same reason: one key per
       button per page life, so a double click, a second tab or a retry after
       a timeout all resolve to ONE coupon rather than several. The offer id
       is sent; the percentage, the eligible products and the ceiling are the
       server's to re-read, because a value posted from a browser is a
       suggestion. */
    root.querySelectorAll("[data-calmy-club-claim]").forEach(function (btn) {
      var key = null;
      var inflight = false;
      btn.addEventListener("click", function () {
        if (btn.disabled || inflight) return;
        inflight = true;
        var idle = btn.textContent;
        btn.disabled = true;
        btn.setAttribute("aria-busy", "true");
        if (!key) {
          key = (window.crypto && window.crypto.randomUUID)
            ? window.crypto.randomUUID()
            : String(Date.now()) + "-" + Math.random().toString(16).slice(2);
        }
        fetch(CLAIM, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ offer_id: btn.getAttribute("data-calmy-club-claim"), idempotency_key: key })
        })
          .then(function (res) {
            return res.json().then(function (b) { return { ok: res.ok, body: b }; },
                                   function () { return { ok: false, body: null }; });
          })
          .then(function (r) {
            btn.removeAttribute("aria-busy");
            if (r.ok && r.body && r.body.code) {
              /* A code, so a code row replaces the button. Rendered from what
                 the server returned, never from anything assembled here. */
              var wrap = document.createElement("div");
              wrap.className = "cclub__ocode";
              var span = document.createElement("span");
              span.className = "cclub__ocodev";
              span.setAttribute("data-calmy-club-code", "");
              span.textContent = r.body.code;
              var cp = document.createElement("button");
              cp.type = "button";
              cp.className = "cclub__ocopy";
              cp.setAttribute("data-calmy-club-copy", "");
              cp.textContent = btn.getAttribute("data-copy-label") || "Copy";
              wrap.appendChild(span);
              wrap.appendChild(cp);
              btn.parentNode.replaceChild(wrap, btn);
              initCopy(wrap.parentNode);
              return;
            }
            inflight = false;
            btn.disabled = false;
            btn.textContent = idle;
            say(root, (r.body && r.body.message) || CT("club_offer_failed", "That did not go through. The offer is still yours."));
          })
          .catch(function () {
            btn.removeAttribute("aria-busy");
            inflight = false;
            btn.disabled = false;
            btn.textContent = idle;
            say(root, CT("club_offer_network", "We could not reach the club. Nothing has changed - try again."));
          });
      });
    });

    /* ---- redemption ---- */
    root.querySelectorAll("[data-calmy-club-redeem]").forEach(function (btn) {
      /* ONE KEY PER BUTTON PER PAGE LIFETIME. The same key is replayed on
         every retry of the same intent, so a double click, a second tab or a
         reissued request after a timeout all resolve to the SAME redemption
         server-side rather than spending the points twice. */
      var key = null;
      var inflight = false;

      btn.addEventListener("click", function () {
        if (btn.disabled || inflight) return;
        inflight = true;
        var idle = btn.textContent;
        btn.disabled = true;
        btn.setAttribute("aria-busy", "true");

        if (!key) {
          key = (window.crypto && window.crypto.randomUUID)
            ? window.crypto.randomUUID()
            : String(Date.now()) + "-" + Math.random().toString(16).slice(2);
        }

        fetch(ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            reward_id: btn.getAttribute("data-calmy-club-redeem"),
            idempotency_key: key
          })
        })
          .then(function (res) {
            return res.json().then(function (body) { return { ok: res.ok, status: res.status, body: body }; },
                                   function () { return { ok: false, status: res.status, body: null }; });
          })
          .then(function (r) {
            btn.removeAttribute("aria-busy");
            /* SUCCESS IS A CODE, NOT A 200. An endpoint that answers without
               issuing anything has not redeemed anything. */
            if (r.ok && r.body && r.body.code) {
              btn.textContent = btn.getAttribute("data-done") || "Redeemed";
              root.dispatchEvent(new CustomEvent("calmy:club:redeemed", { detail: r.body, bubbles: true }));
              return;   // stays disabled: this reward is spent
            }
            inflight = false;
            btn.disabled = false;
            btn.textContent = idle;
            say(root, (r.body && r.body.message) || CT("club_redeem_failed", "That did not go through. Your points have not been spent."));
          })
          .catch(function () {
            /* A request that never completed may or may not have been applied.
               The key is kept, so pressing again replays the SAME intent and
               the server resolves it once. Never reported as success. */
            btn.removeAttribute("aria-busy");
            inflight = false;
            btn.disabled = false;
            btn.textContent = idle;
            say(root, CT("club_redeem_network", "We could not reach the club. Nothing has been spent - try again."));
          });
      });
    });
  }

  /* ── the new-offer chip ──────────────────────────────────────────────────
     Small, dismissible, and deliberately unambitious. It never opens itself,
     never covers the page, and is not shown while another panel is already
     open or anywhere near the cart, because a notification that interrupts
     somebody mid-checkout is worse than one they never see.

     WHAT IS STORED is the ids of campaigns already acknowledged. That is a
     preference about a chip, not a statement about eligibility — the offers
     themselves come from the customer's own record and are unaffected by
     anything written here. */
  var SEEN = "calmy_club_seen_campaigns";

  function seenSet() {
    try {
      var raw = window.localStorage.getItem(SEEN);
      var l = raw ? JSON.parse(raw) : [];
      return Array.isArray(l) ? l : [];
    } catch (e) { return []; }
  }
  function remember(ids) {
    try {
      var all = seenSet();
      ids.forEach(function (i) { if (all.indexOf(i) < 0) all.push(i); });
      window.localStorage.setItem(SEEN, JSON.stringify(all.slice(-40)));
    } catch (e) {}
  }

  function initNotify(root) {
    if (root.getAttribute("data-club-notify") !== "1") return;
    var raw = (root.getAttribute("data-club-campaigns") || "").trim();
    if (!raw) return;
    /* Nowhere near a checkout, and not on top of an open panel. */
    if (/\/(checkouts?|cart)(\/|$)/.test(window.location.pathname)) return;
    if (document.body.classList.contains("calmy-lock")) return;

    var ids = raw.split(",").map(function (x) { return x.trim(); }).filter(Boolean);
    var seen = seenSet();
    var fresh = ids.filter(function (i) { return seen.indexOf(i) < 0; });
    if (!fresh.length) return;

    var chip = document.createElement("div");
    chip.className = "cclub__note-chip";
    chip.setAttribute("role", "status");
    var text = document.createElement("span");
    text.textContent = root.getAttribute("data-club-notify-text") || "You have a new offer";
    var open = document.createElement("button");
    open.type = "button";
    open.className = "cclub__note-open";
    open.textContent = root.getAttribute("data-club-notify-cta") || CT("club_see_it", "See it");
    var x = document.createElement("button");
    x.type = "button";
    x.className = "cclub__note-x";
    x.setAttribute("aria-label", "Dismiss");
    x.textContent = "\u00d7";
    chip.appendChild(text);
    chip.appendChild(open);
    chip.appendChild(x);

    function close() { remember(fresh); if (chip.parentNode) chip.parentNode.removeChild(chip); }
    x.addEventListener("click", close);
    open.addEventListener("click", function () {
      close();
      /* Opens their offers, on the For you tab, through the same opener every
         other Club entry point uses. */
      if (window.Calmy && window.Calmy.open) window.Calmy.open("[data-calmy-club]");
      var f = root.querySelector('[data-calmy-club-tab="f"][role="tab"]');
      if (f) window.setTimeout(function () { f.click(); }, 60);
    });
    document.body.appendChild(chip);
  }

  function say(root, msg) {
    var box = root.querySelector("[data-calmy-club-msg]");
    if (!box) {
      box = document.createElement("p");
      box.className = "cclub__note";
      box.setAttribute("role", "alert");
      box.setAttribute("data-calmy-club-msg", "");
      var body = root.querySelector(".cclub__body");
      if (body) body.appendChild(box);
    }
    box.textContent = msg;
  }

  /* ---- product page earning estimate ----
     Same two sources the rest of the product page uses: the variants JSON and
     the hidden input the form actually submits. So the estimate always
     describes the variant that would be added, including a bundle tier. */
  function initEarn(row) {
    if (row.__calmyEarnDone) return;
    row.__calmyEarnDone = true;
    var section = row.closest(".shopify-section") || document;
    var idInput = section.querySelector("[data-calmy-id]");
    var dataEl = section.querySelector("[data-calmy-variants]");
    var out = row.querySelector("[data-calmy-club-earn-text]");
    if (!idInput || !dataEl || !out) return;

    var payload;
    try { payload = JSON.parse(dataEl.textContent); } catch (e) { return; }
    var variants = (payload && payload.variants) || [];
    var rate = parseFloat(row.getAttribute("data-rate"));
    if (!(rate > 0)) rate = 1;
    var tpl = row.getAttribute("data-template") || "Earn approximately [points] points with this purchase.";

    function update() {
      var v = null;
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(idInput.value)) { v = variants[i]; break; }
      }
      if (!v) { row.hidden = true; return; }
      /* Integer money: minor units in, floor at the end. Never a float total. */
      var pts = Math.floor((parseInt(v.price, 10) / 100) * rate);
      if (!(pts > 0)) { row.hidden = true; return; }
      out.textContent = tpl.replace("[points]", String(pts));
      row.hidden = false;
    }
    update();
    if (window.MutationObserver) {
      new MutationObserver(update).observe(idInput, { attributes: true, attributeFilter: ["value"] });
    }
    section.addEventListener("change", function () { window.setTimeout(update, 0); });
  }

  function boot(root) {
    (root || document).querySelectorAll("[data-calmy-club]").forEach(init);
    (root || document).querySelectorAll("[data-calmy-club-earn]").forEach(initEarn);
    /* COMING BACK FROM SIGN-IN, with the hint intact.

       This used to be a single `window.Calmy && window.Calmy.open` check, and
       it never fired. Both files are deferred and they execute in document
       order — calmy-club.js is emitted before calmy-core.js — so at boot the
       opener does not exist yet, the guard was false, and the branch was
       skipped in silence. The drawer stayed parked off-screen at
       translateX(474px) while ?club=open sat in the address bar.

       So it waits for the opener rather than assuming it. A bounded number of
       frames, because a wait with no end is how a page ends up spinning
       forever over a script that failed to load. */
    try {
      if (/[?&]club=open/.test(location.search)) {
        var tries = 0;
        (function whenReady() {
          if (window.Calmy && window.Calmy.open) {
            window.Calmy.open("[data-calmy-club]", null);
            return;
          }
          if (++tries > 60) return;
          window.requestAnimationFrame(whenReady);
        })();
      }
    } catch (e) {}
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", function () { boot(); });
  else boot();
  document.addEventListener("shopify:section:load", function (e) { boot(e.target); });
})();
